/**
 * Error Sanitization Utilities
 * 
 * SECURITY: Provides sanitized error logging to prevent
 * sensitive data exposure in browser console logs.
 * 
 * @see security finding: client_error_detail
 */

/**
 * Patterns that indicate sensitive data that should be redacted
 */
const SENSITIVE_PATTERNS = [
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, // UUIDs
  /eyJ[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_.+/]*/g, // JWT tokens
  /Bearer\s+[A-Za-z0-9-_.]+/gi, // Bearer tokens
  /password[=:]\s*[^\s&]+/gi, // Password params
  /key[=:]\s*[A-Za-z0-9_-]{20,}/gi, // API keys
  /secret[=:]\s*[^\s&]+/gi, // Secrets
  /token[=:]\s*[^\s&]+/gi, // Tokens
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, // Email addresses
  /\b\d{3}[-.]?\d{2}[-.]?\d{4}\b/g, // SSN patterns
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, // Phone numbers
];

/**
 * Database/internal paths that should be redacted
 */
const INTERNAL_PATH_PATTERNS = [
  /\/home\/[^\s]+/g, // Linux paths
  /[A-Z]:\\[^\s]+/g, // Windows paths
  /supabase\/[^\s]+/g, // Supabase internal paths
  /node_modules\/[^\s]+/g, // Node modules paths
];

/**
 * Redact sensitive information from a string
 */
function redactSensitiveData(text: string): string {
  let redacted = text;
  
  // Redact UUIDs with placeholder
  SENSITIVE_PATTERNS.forEach((pattern) => {
    redacted = redacted.replace(pattern, '[REDACTED]');
  });
  
  // Redact internal paths
  INTERNAL_PATH_PATTERNS.forEach((pattern) => {
    redacted = redacted.replace(pattern, '[PATH]');
  });
  
  return redacted;
}

/**
 * Sanitized error information safe for console logging
 */
export interface SanitizedError {
  message: string;
  code?: string;
  name?: string;
  timestamp: string;
}

/**
 * Sanitize an error object for safe console logging
 * 
 * SECURITY: Removes sensitive data like UUIDs, tokens, emails, 
 * internal paths, and other potentially sensitive information
 * before logging to browser console.
 * 
 * @param error - The error to sanitize
 * @returns Sanitized error object safe for logging
 */
export function sanitizeError(error: unknown): SanitizedError {
  const timestamp = new Date().toISOString();
  
  if (error === null || error === undefined) {
    return { message: 'Unknown error', timestamp };
  }
  
  if (typeof error === 'string') {
    return { 
      message: redactSensitiveData(error), 
      timestamp 
    };
  }
  
  if (error instanceof Error) {
    const errorWithCode = error as Error & { code?: string };
    return {
      message: redactSensitiveData(error.message),
      name: error.name,
      code: errorWithCode.code,
      timestamp,
    };
  }
  
  // Handle objects with message property
  if (typeof error === 'object' && 'message' in error) {
    const errObj = error as { message?: string; code?: string; name?: string };
    return {
      message: redactSensitiveData(String(errObj.message || 'Unknown error')),
      code: errObj.code,
      name: errObj.name,
      timestamp,
    };
  }
  
  return { 
    message: 'Non-standard error object', 
    timestamp 
  };
}

/**
 * Safe console.error wrapper that sanitizes error data
 * 
 * Use this instead of console.error() for any error that might
 * contain user data, database responses, or internal paths.
 * 
 * @param context - Context string describing where the error occurred
 * @param error - The error to log (will be sanitized)
 */
export function safeConsoleError(context: string, error: unknown): void {
  const sanitized = sanitizeError(error);
  console.error(`[${context}]`, sanitized);
}

/**
 * Safe console.warn wrapper that sanitizes error data
 */
export function safeConsoleWarn(context: string, error: unknown): void {
  const sanitized = sanitizeError(error);
  console.warn(`[${context}]`, sanitized);
}
